import React, { useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import ConfigFieldPanel from 'components/admin/shared/ConfigFieldPanel';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import FormHeader from 'components/shared/form/FormHeader';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import Launcher from 'components/shared/Launcher';
import { SUMMARY } from 'constants/aiPrompts';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import { simpleDateAtTime } from 'lib/date';
import type { AIPrompt } from 'models/flexible/AIPrompt';
import { Client } from 'models/flexible/Client';
import type { Group } from 'models/flexible/Group';
import { PERMISSIONS } from 'models/flexible/Profile';
import {
  adminAIPromptsQueryKeys,
  adminShowAIPrompt,
  createAIPrompt,
  updateAIPrompt,
} from 'services/api/admin/aiPrompts';
import type { AdminAIPromptShowFields, AdminGroupIndexFields } from 'services/api/fields';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

import AssociatedClientsSlideout from '../workspacePrompts/AssociatedClientsSlideout';
import AssociatedGroupsSlideout from '../workspacePrompts/AssociatedGroupsSlideout';

import PromptPublishModal from './PromptPublishModal';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type ConfigFieldType = 'explainer' | 'outputDefinition';

const CONFIG_FIELD_LABELS: Record<ConfigFieldType, { title: string; placeholder: string }> = {
  explainer: { title: 'Explainer', placeholder: 'Enter explainer text...' },
  outputDefinition: { title: 'Output Structure', placeholder: 'Enter output structure...' },
};

const SHOW_INCLUDES = ['client', 'clientIds', 'groups'] as const;

const schema = yup.object({
  name: yup.string().required('Name is required'),
  displayName: yup.string().required('Display Name is required'),
  description: yup.string().optional(),
  clientIds: yup.array().of(yup.string()).min(1, 'Must have at least one client'),
  groupIds: yup.array().of(yup.string()),
  config: yup.object({
    explainer: yup.string().required('Explainer is required'),
    outputDefinition: yup.string().required('Output Structure is required'),
  }),
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type EditPromptFormProps = {
  targetClassname: string;
  promptLabel: string;
  backPath: string;
  providerGroupsOnly?: boolean;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditPromptForm({
  targetClassname,
  promptLabel,
  backPath,
  providerGroupsOnly = false,
}: EditPromptFormProps) {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const isCopyMode = searchParams.get('copy') === 'true';
  const isEditing = !!id && !isCopyMode;
  const canEdit = profile.has(PERMISSIONS.adminAIPromptEdit);
  const canPublish = profile.has(PERMISSIONS.adminAIPromptPublish);
  const readOnly = isEditing && !canEdit;

  // --- Data + form ---

  // Track published state via ref so it's available to useAdminForm's toPayload
  // without circular references. The ref is updated after each render and the hook
  // reads config from a ref internally, so toPayload always sees the latest value.
  const isPublishedRef = useRef(false);

  const form = useAdminForm({
    id,
    show: adminShowAIPrompt,
    create: createAIPrompt,
    update: isCopyMode ? (_id: string, payload: any) => createAIPrompt(payload) : updateAIPrompt,
    include: SHOW_INCLUDES,
    queryKey: adminAIPromptsQueryKeys,
    schema,
    toFormValues: (data) => ({
      id: data?.id ?? '',
      name: data?.name ?? '',
      displayName: data?.displayName ?? '',
      description: data?.description ?? '',
      clientIds: isCopyMode ? [] : (data?.clientIds ?? []),
      groupIds: isCopyMode ? [] : (data?.groups?.map((g) => g.id) ?? []),
      config: {
        explainer: data?.config?.explainer ?? '',
        outputDefinition: data?.config?.outputDefinition ?? '',
        explainerLastUpdated: data?.config?.explainerLastUpdated as Date | undefined,
        outputDefinitionLastUpdated: data?.config?.outputDefinitionLastUpdated as Date | undefined,
      },
      isPublished: (data?.isPublished && !isCopyMode) || false,
      publishedAt: (data?.publishedAt ?? null) as Date | null,
      updatedAt: (data?.updatedAt ?? null) as Date | null,
      groups: data?.groups ?? [],
    }),
    toPayload: (v) => {
      if (isPublishedRef.current) {
        return {
          displayName: v.displayName ?? '',
          description: v.description ?? '',
          explainer: v.config?.explainer ?? '',
          clientIds: v.clientIds ?? [],
          groupIds: v.groupIds ?? [],
        };
      }
      return {
        name: v.name ?? '',
        displayName: v.displayName ?? '',
        description: v.description ?? '',
        type: SUMMARY,
        targetClassname,
        clientIds: v.clientIds ?? [],
        groupIds: v.groupIds ?? [],
        groupTypeIds: [],
        planTypeClassificationIds: [],
        episodeClassificationIds: [],
        explainer: v.config?.explainer ?? '',
        outputDefinition: v.config?.outputDefinition ?? '',
        include: SHOW_INCLUDES.join(','),
      };
    },
    onCreateSuccess: { toast: `${promptLabel} prompt successfully added!`, navigate: backPath },
    onUpdateSuccess: isCopyMode
      ? { toast: `${promptLabel} prompt successfully added!`, navigate: backPath }
      : { toast: `${promptLabel} prompt successfully updated!`, navigate: backPath },
    onError: { toast: `There was an error saving the ${promptLabel.toLowerCase()} prompt. Please try again.` },
  });

  const { values, valid, dirty, setValue, submit, loading } = form;
  const isPublished = !!form.data?.isPublished && !isCopyMode;
  const latestProcessPromptStatus = form.data?.latestProcessPromptStatus ?? null;
  const isProcessPromptComplete = latestProcessPromptStatus === null || latestProcessPromptStatus === 'completed';
  const locked = readOnly || isPublished;
  const canEditAssociations = !readOnly && (canEdit || isCopyMode);

  // Keep ref in sync for the next render's config
  isPublishedRef.current = isPublished;

  // --- Local state ---

  const [publishingPrompt, setPublishingPrompt] = useState<AIPrompt<AdminAIPromptShowFields> | null>(null);
  const [activeConfig, setActiveConfig] = useState<ConfigFieldType | null>(null);
  const [showClientSlideout, setShowClientSlideout] = useState(false);
  const [showGroupSlideout, setShowGroupSlideout] = useState(false);
  const [addedClients, setAddedClients] = useState<Client[]>([]);
  const [removedClientIds, setRemovedClientIds] = useState<string[]>([]);

  const associatedGroups = (values.groups ?? []) as Group[];

  const originalClientIds = useMemo(
    () => (isCopyMode ? [] : (form.data?.clientIds ?? [])),
    [form.data?.clientIds, isCopyMode]
  );
  const hasAssociationChanges = addedClients.length > 0 || removedClientIds.length > 0;
  const clientIds = values?.clientIds ?? [];
  const groupIds = values?.groupIds ?? [];
  const singleClientId = clientIds.length === 1 ? clientIds[0] : undefined;
  const prevClientIdRef = useRef(singleClientId);

  const handleApplyClients = (newAdded: Client[], newRemoved: string[]) => {
    const originalIds = originalClientIds;
    const addedIds = newAdded.map((c) => c.id);
    const updatedIds = originalIds.filter((cid) => !newRemoved.includes(cid)).concat(addedIds);
    setValue('clientIds', updatedIds);
    setAddedClients(newAdded);
    setRemovedClientIds(newRemoved);
    if (prevClientIdRef.current !== (updatedIds.length === 1 ? updatedIds[0] : undefined)) {
      prevClientIdRef.current = updatedIds.length === 1 ? updatedIds[0] : undefined;
      setValue('groupIds', []);
      setValue('groups', []);
    }
  };

  const handleApplyGroups = (groups: Group<AdminGroupIndexFields<'groupType'>>[]) => {
    setValue('groups', groups);
    setValue(
      'groupIds',
      groups.map((g) => g.id)
    );
  };

  const getSubLabel = (fieldType: ConfigFieldType) => {
    const key = `${fieldType}LastUpdated` as keyof typeof values.config;
    const date = values.config?.[key] as Date | undefined;
    return date ? `Last updated: ${simpleDateAtTime(date)}` : 'Last updated: —';
  };

  const handleSubmit = async (publishAfter = false) => {
    try {
      await submit();
      if (publishAfter) {
        const returned = await adminShowAIPrompt(id!, undefined, SHOW_INCLUDES);
        if (returned.isProcessPromptComplete) {
          setPublishingPrompt(returned);
        } else {
          addToast({
            text: 'The prompt is still being processed. Please wait for processing to complete before publishing.',
          });
          form.goBack();
        }
      } else {
        form.goBack();
      }
    } catch {
      // Error toast is handled by useAdminForm's onError lifecycle
    }
  };

  const isSubmitDisabled = (!dirty && !hasAssociationChanges) || !valid;

  // --- Render ---

  if (id && loading) return <CircularProgress style={{ margin: '48px auto', display: 'block' }} />;

  return (
    <>
      <AdminFormPage>
        <FormHeader
          title={
            readOnly
              ? `View ${promptLabel} Prompt`
              : isEditing
                ? `Edit ${promptLabel} Prompt`
                : `Add ${promptLabel} Prompt`
          }
        />
        {(isEditing || readOnly) && (
          <PublishedSubtitle>
            Published: <StatusCircle $published={values.isPublished} />
            <span>{values.isPublished ? 'Yes' : 'No'}</span>
            {values.isPublished && values.publishedAt && (
              <>
                <UnboldedSpan>|</UnboldedSpan>
                Published At:<UnboldedSpan>{simpleDateAtTime(values.publishedAt)}</UnboldedSpan>
              </>
            )}
            {!values.isPublished && values.updatedAt && (
              <>
                <UnboldedSpan>|</UnboldedSpan>
                Last Updated:<UnboldedSpan>{simpleDateAtTime(values.updatedAt)}</UnboldedSpan>
              </>
            )}
          </PublishedSubtitle>
        )}

        <FormSection>
          <div>
            <InputGroup title='Name'>
              <Input
                value={values.name ?? ''}
                onChange={(e) => setValue('name', e.target.value)}
                placeholder='Enter name'
                disabled={locked}
              />
            </InputGroup>

            <InputGroup title='Display Name'>
              <Input
                value={values.displayName ?? ''}
                onChange={(e) => setValue('displayName', e.target.value)}
                placeholder='Enter display name'
                disabled={!isCopyMode && !canEdit}
              />
            </InputGroup>

            <InputGroup title='Description (optional)'>
              <StyledTextarea
                value={values.description ?? ''}
                onChange={(e) => setValue('description', e.target.value)}
                placeholder='Enter description'
                disabled={!isCopyMode && !canEdit}
                rows={3}
              />
            </InputGroup>
          </div>

          <div>
            <SectionHeader>Associated Clients</SectionHeader>
            <Launcher
              label='Clients'
              subLabel={`${clientIds.length} associated`}
              handleLaunch={() => setShowClientSlideout(true)}
            />
          </div>

          <div>
            <SectionHeader>Associated Groups</SectionHeader>
            <Launcher
              label='Groups'
              subLabel={`${groupIds.length} associated`}
              handleLaunch={() => setShowGroupSlideout(true)}
              disabled={clientIds.length !== 1}
            />
          </div>

          <div>
            <SectionHeader>Summary Prompt</SectionHeader>
            {(['outputDefinition', 'explainer'] as ConfigFieldType[]).map((field) => (
              <Launcher
                key={field}
                label={CONFIG_FIELD_LABELS[field].title}
                subLabel={getSubLabel(field)}
                handleLaunch={() => setActiveConfig(field)}
              />
            ))}
          </div>
        </FormSection>

        <Actions>
          {!readOnly ? (
            <>
              {isEditing && canPublish && (
                <Button
                  variant='outline-primary'
                  onClick={() => handleSubmit(true)}
                  disabled={isSubmitDisabled || !isProcessPromptComplete}
                  loading={!isProcessPromptComplete}>
                  Update and Publish
                </Button>
              )}
              <Spacer />
              <Button variant='ghost' onClick={form.goBack}>
                Cancel
              </Button>
              <Button onClick={() => handleSubmit()} disabled={isSubmitDisabled}>
                {isEditing ? `Update ${promptLabel} Prompt` : `Create ${promptLabel} Prompt`}
              </Button>
            </>
          ) : (
            <>
              <Spacer />
              <Button onClick={form.goBack}>Go Back</Button>
            </>
          )}
        </Actions>
      </AdminFormPage>

      <Foldout open={!!activeConfig} setOpen={(o) => !o && setActiveConfig(null)} width='min(85%, 917px)'>
        {activeConfig && (
          <ConfigFieldPanel
            title={CONFIG_FIELD_LABELS[activeConfig].title}
            placeholder={CONFIG_FIELD_LABELS[activeConfig].placeholder}
            value={values.config?.[activeConfig] ?? ''}
            readOnly={activeConfig === 'explainer' ? !isCopyMode && !canEdit : locked}
            onApply={(val) => setValue(`config.${activeConfig}`, val)}
            onClose={() => setActiveConfig(null)}
          />
        )}
      </Foldout>

      {showClientSlideout && (
        <AssociatedClientsSlideout
          promptId={values.id ?? ''}
          promptName={values.displayName ?? ''}
          handleClose={() => setShowClientSlideout(false)}
          isEditing={canEditAssociations}
          isCopying={isCopyMode}
          isPublished={values.isPublished ?? false}
          handleApply={handleApplyClients}
          initialAddedClients={addedClients}
          initialRemovedClientIds={removedClientIds}
        />
      )}

      {showGroupSlideout &&
        (canEditAssociations ? (
          <AssociatedGroupsSlideout
            promptId={values.id ?? ''}
            promptName={values.displayName ?? ''}
            handleClose={() => setShowGroupSlideout(false)}
            isEditing
            handleApply={handleApplyGroups}
            initialGroups={associatedGroups}
            clientId={singleClientId}
            isProvider={providerGroupsOnly}
          />
        ) : (
          <AssociatedGroupsSlideout
            promptId={values.id ?? ''}
            promptName={values.displayName ?? ''}
            handleClose={() => setShowGroupSlideout(false)}
          />
        ))}

      {publishingPrompt && (
        <PromptPublishModal
          isOpen={!!publishingPrompt}
          prompt={publishingPrompt}
          promptLabel={promptLabel}
          onCancel={() => {
            setPublishingPrompt(null);
            form.goBack();
          }}
          onComplete={() => {
            setPublishingPrompt(null);
            form.goBack();
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const PublishedSubtitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  margin-top: -24px;
  margin-bottom: 24px;
`;

const StatusCircle = styled.div<{ $published: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 8px;
  background-color: ${({ $published }) => ($published ? colors.green100 : colors.black25)};
`;

const UnboldedSpan = styled.span`
  font-weight: 400;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  padding: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: none;

  &::placeholder {
    color: ${colors.black25};
  }

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const Actions = styled(ButtonGroup)`
  justify-content: flex-end;
  width: 100%;
  margin-top: 16px;
`;

const Spacer = styled.div`
  flex: 1;
`;
